import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, Target, TrendingUp, CheckCircle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { PageHead } from "@/components/seo/PageHead";

export default function CaseBeauteCapillaire() {
  const navigate = useNavigate();
  const kpis = [
    { label: "CTR / CVR", description: "Amélioration des taux de clic et conversion" },
    { label: "Part organique vs paid", description: "Équilibre trafic naturel/payant" },
    { label: "Unités par panier", description: "Augmentation panier moyen" },
    { label: "Taux de retour client", description: "Fidélisation et rétention" }
  ];

  const deliverables = [
    "Mapping sémantique & gabarits titres/bullets",
    "A+ modulaire (before/after, bénéfices, réassurance)",
    "Store de marque (gamme, rituels, preuves)",
    "Pack Amazon Ads (archi campagnes, négatifs, dashboards)"
  ];

  const timeline = [
    {
      period: "30 jours",
      description: "Refonte sémantique, A+ v1, restructuration SP/SB, quick wins SEO."
    },
    {
      period: "90 jours", 
      description: "A/B tests créa, Store v2, montée en gamme SB/SD, rituel de reporting."
    }
  ];

  return (
    <>
      <PageHead
        title="Cas Beauté Capillaire DNVB — Repère cheveux sains | Cosmos Wave"
        description="Étude Creative Strategist : transformation d'un produit beauté en repère « cheveux sains » avec plateforme de marque et optimisation Amazon."
        canonical="https://www.cosmos-wave.com/cas/beaute-capillaire-dnvb"
        pageType="article"
      />
      <div className="min-h-screen bg-background">

      {/* Hero Section */}
      <section className="py-12 lg:py-16 xl:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-card/10 via-transparent to-card/10" />
        <div className="container mx-auto px-6 xl:px-8 2xl:px-12 relative">
          <div className="max-w-4xl xl:max-w-5xl mx-auto text-center">
            <Badge variant="outline" className="text-primary border-primary/30 bg-primary/10 mb-6">
              DNVB · Beauté capillaire
            </Badge>
            <h1 className="text-4xl md:text-5xl xl:text-6xl 2xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              De produit noyé à repère « cheveux sains »
            </h1>
            <p className="text-xl xl:text-2xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
              Plateforme de marque, A+ & Store, sémantique et Ads orientés conversion.
            </p>
            <Button variant="cta-primary" size="xl" onClick={() => navigate("/audit-gratuit")} className="mb-8">
              Demander une présentation
            </Button>
            
            {/* Scroll indicator */}
            <div className="flex flex-col items-center mt-12 lg:mt-16">
              <p className="text-sm text-muted-foreground mb-3">Découvrez l'étude complète</p>
              <div className="w-6 h-10 border-2 border-primary/30 rounded-full flex justify-center">
                <div className="w-1 h-3 bg-primary rounded-full mt-2 animate-bounce"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Values Section */}
      <section className="py-16 bg-card/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              ["Défi", "Produit noyé dans le mass merch, promesse floue, assets hétérogènes."],
              ["Intuition", "L'acheteuse cherche des codes salon + bénéfices santé immédiats."],
              ["Intervention", "Renaming ciblé, identité clean-beauty, SEO listings, A+ modulaire bénéfices, Store immersif, structure SP/SB/SD."],
              ["Résultat", "Position « cheveux sains »; hausse nette CTR/CVR et panier moyen (qualitatif)."]
            ].map(([key, value], index) => (
              <Card key={key} className="p-6 border-border bg-card backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-primary mb-2">{key}</h3>
                    <p className="text-muted-foreground leading-relaxed">{value}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight mb-8 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-primary" />
              Livrables clés
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {deliverables.map((deliverable, index) => (
                <Card key={index} className="p-4 border-border bg-card/50">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-foreground">{deliverable}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-card/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight mb-8 flex items-center gap-3">
              <Clock className="w-8 h-8 text-primary" />
              Roadmap
            </h2>
            <div className="space-y-6">
              {timeline.map((item, index) => (
                <Card key={index} className="p-6 border-border bg-card backdrop-blur-sm">
                  <div className="flex items-start gap-4">
                    <Badge variant="outline" className="text-primary border-primary/30 bg-primary/10 flex-shrink-0">
                      {item.period}
                    </Badge>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* KPIs Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight mb-8 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-primary" />
              KPIs suivis
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {kpis.map((kpi, index) => (
                <Card key={index} className="p-6 border-border bg-card/50 hover:bg-card transition-colors">
                  <h3 className="font-semibold text-foreground mb-2">{kpi.label}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{kpi.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Note Section */}
      <section className="py-16 bg-card/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 border-primary/20 bg-primary/5 text-center">
              <Target className="w-12 h-12 text-primary mx-auto mb-4" />
              <p className="text-foreground font-medium">
                Projet présenté sous NDA — chiffres détaillés disponibles en rendez-vous.
              </p>
              <Button 
                variant="cta-primary" 
                className="mt-4"
                onClick={() => navigate("/audit-gratuit")}
              >
                Prendre rendez-vous
              </Button>
            </Card>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}